<template>
  <div>
    <!-- Mobile Top Navbar -->
    <div 
      v-if="isMobile"
      class="mobile-navbar"
      :class="{ 'expanded': isOpen }"
    >
      <div class="navbar-header">
        <img class="logo-img" src="/Selgate-Logo-removebg-preview.png" alt="Selgate Corporation Logo" />
        <button class="toggle-btn" @click="toggleSidebar">
          <i class="fa-solid" :class="isOpen ? 'fa-xmark' : 'fa-bars'"></i>
        </button>
      </div>
      <nav class="navbar-nav" v-show="isOpen">
        <ul>
          <template v-if="isManagement">
            <li>
              <router-link to="/analytics/yearly" class="nav-item" exact @click="handleNavClick">
                <i class="fa-solid fa-chart-column"></i>
                <span class="text">Yearly Analytics</span>
              </router-link>
            </li>
            <li>
              <div class="nav-item overview-item" @click="toggleSubmenu('nonClinicalDashboard')">
                <i class="fa-solid fa-chart-line"></i>
                <span class="text">Non Clinical</span>
                <i class="fa-solid" :class="openSubmenus.nonClinicalDashboard ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
              </div>
              <transition name="slide">
                <ul class="submenu" v-if="openSubmenus.nonClinicalDashboard">
                  <li>
                    <router-link
                      to="/dashboard"
                      class="submenu-link dashboard-submenu-link"
                      :class="{ 'submenu-link-active': route.path === '/dashboard' && nonClinicalSelected === 'all' }"
                      exact
                      @click.stop="pickNonClinicalSubsidiary('all')"
                    >
                      <span class="text">All Subsidiaries</span>
                    </router-link>
                  </li>
                  <li v-for="sub in nonClinicalSubs" :key="sub.id">
                    <router-link
                      to="/dashboard"
                      class="submenu-link dashboard-submenu-link"
                      :class="{ 'submenu-link-active': route.path === '/dashboard' && nonClinicalSelected == sub.id }"
                      exact
                      @click.stop="pickNonClinicalSubsidiary(sub.id)"
                    >
                      <span class="text">{{ sub.name }}</span>
                    </router-link>
                  </li>
                </ul>
              </transition>
            </li>
            <li>
              <div class="nav-item overview-item" @click="toggleSubmenu('clinicalDashboard')">
                <i class="fa-solid fa-heart-pulse"></i>
                <span class="text">Clinical</span>
                <i class="fa-solid" :class="openSubmenus.clinicalDashboard ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
              </div>
              <transition name="slide">
                <ul class="submenu" v-if="openSubmenus.clinicalDashboard">
                  <li>
                    <router-link
                      to="/dashboard/hospital"
                      class="submenu-link dashboard-submenu-link"
                      :class="{ 'submenu-link-active': route.path === '/dashboard/hospital' && clinicalSelected === 'all' }"
                      exact
                      @click.stop="pickClinicalSubsidiary('all')"
                    >
                      <span class="text">All Subsidiaries</span>
                    </router-link>
                  </li>
                  <li v-for="sub in clinicalSubs" :key="sub.id">
                    <router-link
                      to="/dashboard/hospital"
                      class="submenu-link dashboard-submenu-link"
                      :class="{ 'submenu-link-active': route.path === '/dashboard/hospital' && clinicalSelected == sub.id }"
                      exact
                      @click.stop="pickClinicalSubsidiary(sub.id)"
                    >
                      <span class="text">{{ sub.name }}</span>
                    </router-link>
                  </li>
                </ul>
              </transition>
            </li>
            <li>
              <router-link to="/management/rawang-dashboard" class="nav-item" exact @click="handleNavClick">
                <i class="fa-solid fa-hospital"></i>
                <span class="text">Selgate Hospital Dashboard</span>
              </router-link>
            </li>
          </template>
          <template v-else-if="isProcurement">
            <li>
              <router-link
                to="/warehouse"
                class="nav-item"
                active-class=""
                exact-active-class="router-link-active"
                @click="handleNavClick"
              >
                <i class="fa-solid fa-chart-line"></i>
                <span class="text">Dashboard</span>
              </router-link>
            </li>
            <li>
              <router-link to="/warehouse/inventory" class="nav-item" @click="handleNavClick">
                <i class="fa-solid fa-boxes-stacked"></i>
                <span class="text">Inventory</span>
              </router-link>
            </li>
            <li>
              <router-link to="/warehouse/clinic" class="nav-item" @click="handleNavClick">
                <i class="fa-solid fa-clinic-medical"></i>
                <span class="text">Clinic</span>
              </router-link>
            </li>
            <li>
              <router-link to="/warehouse/dental" class="nav-item" @click="handleNavClick">
                <i class="fa-solid fa-tooth"></i>
                <span class="text">Dental</span>
              </router-link>
            </li>
            <li>
              <router-link to="/warehouse/orders" class="nav-item" @click="handleNavClick">
                <i class="fa-solid fa-truck-medical"></i>
                <span class="text">Orders & DO</span>
              </router-link>
            </li>
            <li>
              <router-link to="/warehouse/sales" class="nav-item" @click="handleNavClick">
                <i class="fa-solid fa-cart-shopping"></i>
                <span class="text">Sales</span>
              </router-link>
            </li>
            <li>
              <router-link to="/warehouse/data-flow" class="nav-item" @click="handleNavClick">
                <i class="fa-solid fa-diagram-project"></i>
                <span class="text">Data Flow</span>
              </router-link>
            </li>
            <li>
              <router-link to="/warehouse/sources" class="nav-item" @click="handleNavClick">
                <i class="fa-solid fa-shuffle"></i>
                <span class="text">Sources & Mapping</span>
              </router-link>
            </li>
          </template>
          <li v-else-if="isFinance">
            <div class="nav-item overview-item" @click="toggleSubmenu('financeDashboard')">
              <i class="fa-solid fa-chart-line"></i>
              <span class="text">Finance Dashboard</span>
              <i class="fa-solid" :class="openSubmenus.financeDashboard ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
            </div>
            <transition name="slide">
              <ul class="submenu" v-if="openSubmenus.financeDashboard">
                <li>
                  <router-link to="/finance/dashboard" class="submenu-link" exact @click.stop="handleNavClick">
                    <span class="text">Finance Dashboard</span>
                  </router-link>
                </li>
                <li>
                  <router-link to="/analytics/yearly" class="submenu-link" exact @click.stop="handleNavClick">
                    <span class="text">Overall</span>
                  </router-link>
                </li>
                <li>
                  <router-link to="/dashboard/hospital" class="submenu-link" exact @click.stop="handleNavClick">
                    <span class="text">Clinical Dashboard</span>
                  </router-link>
                </li>
                <li>
                  <router-link to="/management/rawang-dashboard" class="submenu-link" exact @click.stop="handleNavClick">
                    <span class="text">Hospital Dashboard</span>
                  </router-link>
                </li>
                <li>
                  <router-link to="/dashboard" class="submenu-link" exact @click.stop="handleNavClick">
                    <span class="text">Non Clinical Dashboard</span>
                  </router-link>
                </li>
              </ul>
            </transition>
          </li>
          <li v-if="false && isManagement">
            <router-link to="/management/scms-tracker" class="nav-item" exact @click="handleNavClick">
              <i class="fa-solid fa-chart-gantt"></i>
              <span class="text">SCMS Tracker</span>
            </router-link>
          </li>

          <!-- PIC specific menu items -->
          <template v-if="isPIC">
            <li>
              <router-link to="/pic-daily-report" class="nav-item" exact @click="handleNavClick">
                <i class="fa-solid fa-file-lines"></i>
                <span class="text">Performance Report</span>
              </router-link>
            </li>
            <li>
              <router-link to="/pic-records" class="nav-item" exact @click="handleNavClick">
                <i class="fa-solid fa-folder"></i>
                <span class="text">Manage Records</span>
              </router-link>
            </li>
          </template>
          
          <!-- Admin specific menu items -->
          <template v-if="isAdmin">
            <li>
              <router-link to="/admin-dashboard" class="nav-item" exact @click="handleNavClick">
                <i class="fa-solid fa-gauge-high"></i>
                <span class="text">Admin Dashboard</span>
              </router-link>
            </li>
            <li>
              <router-link to="/user-registration" class="nav-item" exact @click="handleNavClick">
                <i class="fa-solid fa-user-plus"></i>
                <span class="text">User Registration</span>
              </router-link>
            </li>
            <li>
              <router-link to="/admin/subsidiaries" class="nav-item" exact @click="handleNavClick">
                <i class="fa-solid fa-building"></i>
                <span class="text">Subsidiary Management</span>
              </router-link>
            </li>
            <li>
              <router-link to="/admin/users" class="nav-item" exact @click="handleNavClick">
                <i class="fa-solid fa-users-cog"></i>
                <span class="text">User Management</span>
              </router-link>
            </li>
            <li>
              <router-link to="/admin/daily-report" class="nav-item" exact @click="handleNavClick">
                <i class="fa-solid fa-file-lines"></i>
                <span class="text">Performance Report</span>
              </router-link>
            </li>
            <li>
              <router-link to="/admin/performance" class="nav-item" exact @click="handleNavClick">
                <i class="fa-solid fa-chart-simple"></i>
                <span class="text">Performance Management</span>
              </router-link>
            </li>
            <li>
              <router-link to="/admin/revenue" class="nav-item" exact @click="handleNavClick">
                <i class="fa-solid fa-money-bill-wave"></i>
                <span class="text">Revenue Management</span>
              </router-link>
            </li>
          </template>
          
          <!-- Finance specific menu items -->
          <template v-if="isFinance">
            <li>
              <router-link to="/finance/yearly-targets" class="nav-item" exact @click="handleNavClick">
                <i class="fa-solid fa-bullseye"></i>
                <span class="text">Yearly Targets</span>
              </router-link>
            </li>
            <li>
              <router-link to="/finance/yearly-budget" class="nav-item" exact @click="handleNavClick">
                <i class="fa-solid fa-coins"></i>
                <span class="text">Yearly Budget</span>
              </router-link>
            </li>
            <li>
              <router-link to="/finance/cost-submission" class="nav-item" exact @click="handleNavClick">
                <i class="fa-solid fa-money-bill-transfer"></i>
                <span class="text">Cost Submission Management</span>
              </router-link>
            </li>
            <li>
              <router-link to="/finance/subsidiaries" class="nav-item" exact @click="handleNavClick">
                <i class="fa-solid fa-building"></i>
                <span class="text">Subsidiary Management</span>
              </router-link>
            </li>
            <li>
              <router-link to="/finance/admin-management-registration" class="nav-item" exact @click="handleNavClick">
                <i class="fa-solid fa-users-gear"></i>
                <span class="text">Admin & Management Registration</span>
              </router-link>
            </li>
            <li>
              <router-link to="/finance/revenue-report" class="nav-item" exact @click="handleNavClick">
                <i class="fa-solid fa-chart-pie"></i>
                <span class="text">Revenue Report</span>
              </router-link>
            </li>
            <li>
              <router-link to="/finance/revenue-adjustment" class="nav-item" exact @click="handleNavClick">
                <i class="fa-solid fa-sliders"></i>
                <span class="text">Revenue Adjustment</span>
              </router-link>
            </li>
            <li>
              <router-link to="/finance/rawang-kpi" class="nav-item" exact @click="handleNavClick">
                <i class="fa-solid fa-hospital"></i>
                <span class="text">Rawang KPI Input</span>
              </router-link>
            </li>
            <li>
              <router-link to="/finance/scms-tracker" class="nav-item" exact @click="handleNavClick">
                <i class="fa-solid fa-search"></i>
                <span class="text">SCMS Tracker</span>
              </router-link>
            </li>
          </template>
          
          <!-- Only show these items if user is not PIC -->
          <template v-if="!isPIC">
            <!-- <li v-if="isManagement">
              <div class="nav-item overview-item" @click="toggleSubmenu('overview')">
                <i class="fa-solid fa-chart-pie"></i>
                <span class="text" v-if="isOpen">Subsidiaries Overview</span>
                <i class="fa-solid" :class="openSubmenus.overview ? 'fa-chevron-down' : 'fa-chevron-right'" v-if="isOpen"></i>
              </div>
              <transition name="slide">
                <ul class="submenu" v-if="isOpen && openSubmenus.overview">
                  <li v-for="subsidiary in subsidiaries" 
                      :key="subsidiary.id">
                    <router-link :to="`/management/subsidiary/${subsidiary.id}`" class="nav-item submenu-item" exact @click="handleNavClick">
                      <span class="text">{{ subsidiary.name }}</span>
                    </router-link>
                  </li>
                </ul>
              </transition>
            </li> -->
          </template>
          <li>
            <button class="logout-btn" @click="handleLogout">
              <i class="fa-solid fa-right-from-bracket"></i>
              <span class="text">Logout</span>
            </button>
          </li>
        </ul>
      </nav>
    </div>

    <!-- Desktop Sidebar -->
    <div 
      v-else
      class="sidebar" 
      :class="{ 'collapsed': !isOpen }"
    >
      <div class="sidebar-header" :class="{ 'centered': isOpen, 'collapsed-header': !isOpen }">
        <button class="toggle-btn" @click="toggleSidebar">
          <i class="fa-solid" :class="isOpen ? 'fa-xmark' : 'fa-bars'"></i>
        </button>
      </div>
      <nav class="sidebar-nav">
        <ul>
          <template v-if="isManagement">
            <li>
              <router-link to="/analytics/yearly" class="nav-item" exact @click="handleNavClick">
                <i class="fa-solid fa-chart-column"></i>
                <span class="text" v-if="isOpen">Yearly Analytics</span>
              </router-link>
            </li>
            <li>
              <div class="nav-item overview-item" @click="toggleSubmenu('nonClinicalDashboard')">
                <i class="fa-solid fa-chart-line"></i>
                <span class="text" v-if="isOpen">Non Clinical</span>
                <i class="fa-solid" :class="openSubmenus.nonClinicalDashboard ? 'fa-chevron-down' : 'fa-chevron-right'" v-if="isOpen"></i>
              </div>
              <transition name="slide">
                <ul class="submenu" v-if="isOpen && openSubmenus.nonClinicalDashboard">
                  <li>
                    <router-link
                      to="/dashboard"
                      class="submenu-link dashboard-submenu-link"
                      :class="{ 'submenu-link-active': route.path === '/dashboard' && nonClinicalSelected === 'all' }"
                      exact
                      @click.stop="pickNonClinicalSubsidiary('all')"
                    >
                      <span class="text">All Subsidiaries</span>
                    </router-link>
                  </li>
                  <li v-for="sub in nonClinicalSubs" :key="sub.id">
                    <router-link
                      to="/dashboard"
                      class="submenu-link dashboard-submenu-link"
                      :class="{ 'submenu-link-active': route.path === '/dashboard' && nonClinicalSelected == sub.id }"
                      exact
                      @click.stop="pickNonClinicalSubsidiary(sub.id)"
                    >
                      <span class="text">{{ sub.name }}</span>
                    </router-link>
                  </li>
                </ul>
              </transition>
            </li>
            <li>
              <div class="nav-item overview-item" @click="toggleSubmenu('clinicalDashboard')">
                <i class="fa-solid fa-heart-pulse"></i>
                <span class="text" v-if="isOpen">Clinical</span>
                <i class="fa-solid" :class="openSubmenus.clinicalDashboard ? 'fa-chevron-down' : 'fa-chevron-right'" v-if="isOpen"></i>
              </div>
              <transition name="slide">
                <ul class="submenu" v-if="isOpen && openSubmenus.clinicalDashboard">
                  <li>
                    <router-link
                      to="/dashboard/hospital"
                      class="submenu-link dashboard-submenu-link"
                      :class="{ 'submenu-link-active': route.path === '/dashboard/hospital' && clinicalSelected === 'all' }"
                      exact
                      @click.stop="pickClinicalSubsidiary('all')"
                    >
                      <span class="text">All Subsidiaries</span>
                    </router-link>
                  </li>
                  <li v-for="sub in clinicalSubs" :key="sub.id">
                    <router-link
                      to="/dashboard/hospital"
                      class="submenu-link dashboard-submenu-link"
                      :class="{ 'submenu-link-active': route.path === '/dashboard/hospital' && clinicalSelected == sub.id }"
                      exact
                      @click.stop="pickClinicalSubsidiary(sub.id)"
                    >
                      <span class="text">{{ sub.name }}</span>
                    </router-link>
                  </li>
                </ul>
              </transition>
            </li>
            <li>
              <router-link to="/management/rawang-dashboard" class="nav-item" exact @click="handleNavClick">
                <i class="fa-solid fa-hospital"></i>
                <span class="text" v-if="isOpen">Selgate Hospital Dashboard</span>
              </router-link>
            </li>
          </template>
          <template v-else-if="isProcurement">
            <li>
              <router-link
                to="/warehouse"
                class="nav-item"
                active-class=""
                exact-active-class="router-link-active"
                @click="handleNavClick"
              >
                <i class="fa-solid fa-chart-line"></i>
                <span class="text" v-if="isOpen">Dashboard</span>
              </router-link>
            </li>
            <li>
              <router-link to="/warehouse/inventory" class="nav-item" @click="handleNavClick">
                <i class="fa-solid fa-boxes-stacked"></i>
                <span class="text" v-if="isOpen">Inventory</span>
              </router-link>
            </li>
            <li>
              <router-link to="/warehouse/clinic" class="nav-item" @click="handleNavClick">
                <i class="fa-solid fa-clinic-medical"></i>
                <span class="text" v-if="isOpen">Clinic</span>
              </router-link>
            </li>
            <li>
              <router-link to="/warehouse/dental" class="nav-item" @click="handleNavClick">
                <i class="fa-solid fa-tooth"></i>
                <span class="text" v-if="isOpen">Dental</span>
              </router-link>
            </li>
            <li>
              <router-link to="/warehouse/orders" class="nav-item" @click="handleNavClick">
                <i class="fa-solid fa-truck-medical"></i>
                <span class="text" v-if="isOpen">Orders & DO</span>
              </router-link>
            </li>
            <li>
              <router-link to="/warehouse/sales" class="nav-item" @click="handleNavClick">
                <i class="fa-solid fa-cart-shopping"></i>
                <span class="text" v-if="isOpen">Sales</span>
              </router-link>
            </li>
            <li>
              <router-link to="/warehouse/data-flow" class="nav-item" @click="handleNavClick">
                <i class="fa-solid fa-diagram-project"></i>
                <span class="text" v-if="isOpen">Data Flow</span>
              </router-link>
            </li>
            <li>
              <router-link to="/warehouse/sources" class="nav-item" @click="handleNavClick">
                <i class="fa-solid fa-shuffle"></i>
                <span class="text" v-if="isOpen">Sources & Mapping</span>
              </router-link>
            </li>
          </template>
          <li v-else-if="isFinance">
            <div class="nav-item overview-item" @click="toggleSubmenu('financeDashboard')">
              <i class="fa-solid fa-chart-line"></i>
              <span class="text" v-if="isOpen">Finance Dashboard</span>
              <i class="fa-solid" :class="openSubmenus.financeDashboard ? 'fa-chevron-down' : 'fa-chevron-right'" v-if="isOpen"></i>
            </div>
            <transition name="slide">
              <ul class="submenu" v-if="isOpen && openSubmenus.financeDashboard">
                <li>
                  <router-link to="/finance/dashboard" class="submenu-link" exact @click.stop="handleNavClick">
                    <span class="text">Finance Dashboard</span>
                  </router-link>
                </li>
                <li>
                  <router-link to="/analytics/yearly" class="submenu-link" exact @click.stop="handleNavClick">
                    <span class="text">Overall</span>
                  </router-link>
                </li>
                <li>
                  <router-link to="/dashboard/hospital" class="submenu-link" exact @click.stop="handleNavClick">
                    <span class="text">Clinical Dashboard</span>
                  </router-link>
                </li>
                <li>
                  <router-link to="/management/rawang-dashboard" class="submenu-link" exact @click.stop="handleNavClick">
                    <span class="text">Hospital Dashboard</span>
                  </router-link>
                </li>
                <li>
                  <router-link to="/dashboard" class="submenu-link" exact @click.stop="handleNavClick">
                    <span class="text">Non Clinical Dashboard</span>
                  </router-link>
                </li>
              </ul>
            </transition>
          </li>
          <li v-if="false && isManagement">
            <router-link to="/management/scms-tracker" class="nav-item" exact @click="handleNavClick">
              <i class="fa-solid fa-chart-gantt"></i>
              <span class="text" v-if="isOpen">SCMS Tracker</span>
            </router-link>
          </li>

          <!-- PIC specific menu items -->
          <template v-if="isPIC">
            <li>
              <router-link to="/pic-daily-report" class="nav-item" exact>
                <i class="fa-solid fa-file-lines"></i>
                <span class="text" v-if="isOpen">Performance Report</span>
              </router-link>
            </li>
            <li>
              <router-link to="/pic-records" class="nav-item" exact>
                <i class="fa-solid fa-folder"></i>
                <span class="text" v-if="isOpen">Manage Records</span>
              </router-link>
            </li>
          </template>
          
          <!-- Admin specific menu items -->
          <template v-if="isAdmin">
            <li>
              <router-link to="/admin-dashboard" class="nav-item" exact @click="handleNavClick">
                <i class="fa-solid fa-gauge-high"></i>
                <span class="text" v-if="isOpen">Admin Dashboard</span>
              </router-link>
            </li>
            <li>
              <router-link to="/user-registration" class="nav-item" exact @click="handleNavClick">
                <i class="fa-solid fa-user-plus"></i>
                <span class="text" v-if="isOpen">User Registration</span>
              </router-link>
            </li>
            <li>
              <router-link to="/admin/subsidiaries" class="nav-item" exact @click="handleNavClick">
                <i class="fa-solid fa-building"></i>
                <span class="text" v-if="isOpen">Subsidiary Management</span>
              </router-link>
            </li>
            <li>
              <router-link to="/admin/users" class="nav-item" exact @click="handleNavClick">
                <i class="fa-solid fa-users-cog"></i>
                <span class="text" v-if="isOpen">User Management</span>
              </router-link>
            </li>
            <li>
              <router-link to="/admin/daily-report" class="nav-item" exact @click="handleNavClick">
                <i class="fa-solid fa-file-lines"></i>
                <span class="text" v-if="isOpen">Performance Report</span>
              </router-link>
            </li>
            <li>
              <router-link to="/admin/performance" class="nav-item" exact @click="handleNavClick">
                <i class="fa-solid fa-chart-simple"></i>
                <span class="text" v-if="isOpen">Performance Management</span>
              </router-link>
            </li>
            <li>
              <router-link to="/admin/revenue" class="nav-item" exact @click="handleNavClick">
                <i class="fa-solid fa-money-bill-wave"></i>
                <span class="text" v-if="isOpen">Revenue Management</span>
              </router-link>
            </li>
          </template>
          
          <!-- Finance specific menu items -->
          <template v-if="isFinance">
            <li>
              <router-link to="/finance/yearly-targets" class="nav-item" exact @click="handleNavClick">
                <i class="fa-solid fa-bullseye"></i>
                <span class="text">Yearly Targets</span>
              </router-link>
            </li>
            <li>
              <router-link to="/finance/yearly-budget" class="nav-item" exact @click="handleNavClick">
                <i class="fa-solid fa-coins"></i>
                <span class="text">Yearly Budget</span>
              </router-link>
            </li>
            <li>
              <router-link to="/finance/cost-submission" class="nav-item" exact @click="handleNavClick">
                <i class="fa-solid fa-money-bill-transfer"></i>
                <span class="text">Cost Submission Management</span>
              </router-link>
            </li>
            <li>
              <router-link to="/finance/subsidiaries" class="nav-item" exact @click="handleNavClick">
                <i class="fa-solid fa-building"></i>
                <span class="text">Subsidiary Management</span>
              </router-link>
            </li>
            <li>
              <router-link to="/finance/admin-management-registration" class="nav-item" exact @click="handleNavClick">
                <i class="fa-solid fa-users-gear"></i>
                <span class="text">Admin & Management Registration</span>
              </router-link>
            </li>
            <li>
              <router-link to="/finance/revenue-report" class="nav-item" exact @click="handleNavClick">
                <i class="fa-solid fa-chart-pie"></i>
                <span class="text">Revenue Report</span>
              </router-link>
            </li>
            <li>
              <router-link to="/finance/revenue-adjustment" class="nav-item" exact @click="handleNavClick">
                <i class="fa-solid fa-sliders"></i>
                <span class="text">Revenue Adjustment</span>
              </router-link>
            </li>
            <li>
              <router-link to="/finance/rawang-kpi" class="nav-item" exact @click="handleNavClick">
                <i class="fa-solid fa-hospital"></i>
                <span class="text">Rawang KPI Input</span>
              </router-link>
            </li>
            <li>
              <router-link to="/finance/scms-tracker" class="nav-item" exact @click="handleNavClick">
                <i class="fa-solid fa-search"></i>
                <span class="text">SCMS Tracker</span>
              </router-link>
            </li>
          </template>
          
          <!-- Only show these items if user is not PIC -->
          <template v-if="!isPIC">
            <!-- <li v-if="isManagement">
              <div class="nav-item overview-item" @click="toggleSubmenu('overview')">
                <i class="fa-solid fa-chart-pie"></i>
                <span class="text" v-if="isOpen">Subsidiaries Overview</span>
                <i class="fa-solid" :class="openSubmenus.overview ? 'fa-chevron-down' : 'fa-chevron-right'" v-if="isOpen"></i>
              </div>
              <transition name="slide">
                <ul class="submenu" v-if="isOpen && openSubmenus.overview">
                  <li v-for="subsidiary in subsidiaries" 
                      :key="subsidiary.id">
                    <router-link :to="`/management/subsidiary/${subsidiary.id}`" class="nav-item submenu-item" exact @click="handleNavClick">
                      <span class="text">{{ subsidiary.name }}</span>
                    </router-link>
                  </li>
                </ul>
              </transition>
            </li> -->
          </template>
        </ul>
      </nav>
      <div class="sidebar-footer">
        <button class="logout-btn" @click="handleLogout">
          <i class="fa-solid fa-right-from-bracket"></i>
          <span class="text" v-if="isOpen">Logout</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import axios from 'axios'
import { useManagementDashboardSubsidiary } from '@/composables/useManagementDashboardSubsidiary'

const router = useRouter()
const route = useRoute()
const { getState, loadSubsidiaries, setSelectedSubsidiary } = useManagementDashboardSubsidiary()

const nonClinicalSubs = computed(() => getState('non_hospital').subsidiaries.value)
const nonClinicalSelected = computed(() => getState('non_hospital').selectedSubsidiary.value)
const clinicalSubs = computed(() => getState('hospital').subsidiaries.value)
const clinicalSelected = computed(() => getState('hospital').selectedSubsidiary.value)

function pickNonClinicalSubsidiary(id) {
  setSelectedSubsidiary('non_hospital', id)
  if (route.path !== '/dashboard') {
    router.push('/dashboard')
  }
  handleNavClick()
}

function pickClinicalSubsidiary(id) {
  setSelectedSubsidiary('hospital', id)
  if (route.path !== '/dashboard/hospital') {
    router.push('/dashboard/hospital')
  }
  handleNavClick()
}
const isOpen = ref(false)
const isMobile = ref(false)
const openSubmenus = ref({})
const userRole = ref('')
const userSubsidiary = ref(null)
const subsidiaries = ref([])
const subsidiary = ref(null)

// Create axios instance
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
})

// Add auth token to requests
api.interceptors.request.use(config => {
  const token = sessionStorage.getItem('token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

const fetchSubsidiaries = async () => {
  try {
    const response = await api.get('/api/subsidiary')
    subsidiaries.value = response.data
  } catch (error) {
  }
}

const isPIC = computed(() => {
  const userData = sessionStorage.getItem('user')
  if (userData) {
    const user = JSON.parse(userData)
    const isPICUser = user.role.toLowerCase() === 'person-in-charge'
    return isPICUser
  }
  return false
})

const isAdmin = computed(() => {
  const userData = sessionStorage.getItem('user')
  if (userData) {
    const user = JSON.parse(userData)
    return user.role === 'Admin' || user.role === 'admin'
  }
  return false
})

const isFinance = computed(() => {
  const userData = sessionStorage.getItem('user')
  if (userData) {
    const user = JSON.parse(userData)
    const isFinanceUser = user.role === 'Finance' || user.role === 'finance' || user.role?.toLowerCase() === 'finance'
    return isFinanceUser
  }
  return false
})

const isProcurement = computed(() => {
  const userData = sessionStorage.getItem('user')
  if (userData) {
    const user = JSON.parse(userData)
    return String(user.role || '').toLowerCase() === 'procurement'
  }
  return false
})

const isManagement = computed(() => {
  const userData = sessionStorage.getItem('user')
  if (userData) {
    const user = JSON.parse(userData)
    const normalizedRole = String(user.role || '').toLowerCase()
    const isManagementUser =
      normalizedRole === 'management' ||
      normalizedRole.includes('management')
    return isManagementUser
  }
  return false
})

const checkMobile = () => {
  isMobile.value = window.innerWidth <= 768
  if (isMobile.value) {
    isOpen.value = false
  }
}

onMounted(async () => {
  checkMobile()
  window.addEventListener('resize', checkMobile)

  const userData = sessionStorage.getItem('user')
  if (userData) {
    const user = JSON.parse(userData)
    userRole.value = user.role
    userSubsidiary.value = user.subsidiary_id
    subsidiary.value = user.subsidiary_id
    const nr = String(user.role || '').toLowerCase()
    const managementUser = nr === 'management' || nr.includes('management')
    if (managementUser) {
      await Promise.all([loadSubsidiaries('non_hospital'), loadSubsidiaries('hospital')])
    } else if (nr === 'finance') {
      openSubmenus.value.financeDashboard = true
    }
  }
  
  // Fetch subsidiaries
  await fetchSubsidiaries()
  
  // Emit initial state
  emit('toggle', isOpen.value)
})

watch(
  () => [route.path, isManagement.value],
  ([path, mgmt]) => {
    if (!mgmt) return
    if (path === '/dashboard') openSubmenus.value.nonClinicalDashboard = true
    if (path === '/dashboard/hospital') openSubmenus.value.clinicalDashboard = true
  },
  { immediate: true }
)

onUnmounted(() => {
  window.removeEventListener('resize', checkMobile)
})

const getDashboardRoute = () => {
  const userData = sessionStorage.getItem('user')
  if (userData) {
    const user = JSON.parse(userData)
    switch (user.role.toLowerCase()) {
      case 'person-in-charge':
        return '/pic-dashboard'
      case 'admin':
        return '/admin-dashboard'
      case 'management':
        return '/dashboard'
      case 'finance':
        return '/finance/dashboard'
      case 'procurement':
        return '/warehouse'
      default:
        return '/'
    }
  }
  return '/'
}

const toggleSidebar = () => {
  isOpen.value = !isOpen.value
  emit('toggle', isOpen.value)
}

const toggleSubmenu = (submenu) => {
  // Ensure submenu links are clickable on desktop collapsed mode
  if (!isMobile.value && !isOpen.value) {
    isOpen.value = true
    emit('toggle', true)
  }
  openSubmenus.value[submenu] = !openSubmenus.value[submenu]
}

const handleNavClick = () => {
  if (isMobile.value) {
    isOpen.value = false
    emit('toggle', false)
  } else {
    // For desktop: auto-expand sidebar when clicking on collapsed icons
    if (!isOpen.value) {
      isOpen.value = true
      emit('toggle', true)
    }
  }
}

const handleLogout = () => {
  // Clear all authentication data from sessionStorage
  sessionStorage.removeItem('token')
  sessionStorage.removeItem('user')
  
  // Redirect to sign in page
  router.push('/')
}

const emit = defineEmits(['toggle'])

// Helper function to get subsidiary icon
const getSubsidiaryIcon = (id) => {
  const iconMap = {
    1: 'fa-solid fa-hospital', // Selgate Healthcare
    2: 'fa-solid fa-clinic-medical', // Selcare Clinic
    3: 'fa-solid fa-tooth', // Selcare Dental
    4: 'fa-solid fa-stethoscope', // Selcare Diagnostic
    5: 'fa-solid fa-user-nurse', // Selcare Nursing
    6: 'fa-solid fa-pills' // Selcare Pharmacy
  }
  return iconMap[id] || 'fa-solid fa-building' // Default icon for new subsidiaries
}
</script>

<style scoped>
.sidebar {
  height: calc(100vh - 40px);
  width: 250px;
  background: linear-gradient(180deg, #1e40af 60%, #1e3a8a 100%);
  color: var(--text-secondary);
  transition: all 0.3s ease;
  position: fixed;
  left: 20px;
  top: 20px;
  z-index: 100;
  border-radius: var(--spacing-md);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
  margin-right: 20px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.sidebar.collapsed {
  width: 60px;
  margin-right: 20px;
}

.sidebar-header {
  padding: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 70px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  position: sticky;
  top: 0;
  z-index: 1;
}

.sidebar-header.centered,
.sidebar-header.collapsed-header {
  padding: 20px 0;
  justify-content: center;
}

.toggle-btn {
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  font-size: 1.5rem;
  padding: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.toggle-btn:hover {
  background: rgba(255, 255, 255, 0.1);
}

.sidebar-nav {
  padding: 20px 0;
  flex: 1;
  overflow-y: auto;
}

.sidebar-nav ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.nav-item {
  display: flex;
  align-items: flex-start;
  padding: 12px 20px;
  color: var(--text-secondary);
  text-decoration: none;
  transition: none;
  gap: 12px;
  margin: 4px 8px;
  border-radius: 8px;
  -webkit-tap-highlight-color: transparent;
  outline: none;
}

.nav-item:active {
  color: var(--text-secondary);
  background: rgba(255, 255, 255, 0.1);
}

.nav-item i {
  font-size: 1.2rem;
  min-width: 24px;
  text-align: center;
  margin-top: 2px;
  transition: none;
  color: var(--text-secondary);
}

.nav-item:active i {
  color: var(--text-secondary);
}

.sidebar.collapsed .nav-item {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  height: 48px;
  width: 48px;
  min-height: 48px;
  min-width: 48px;
  max-height: 48px;
  max-width: 48px;
  padding: 0 !important;
  margin: 4px auto;
  box-sizing: border-box;
  transition: none;
}

.sidebar.collapsed .nav-item .subsidiary-icon {
  font-size: 1.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 !important;
  padding: 0 !important;
  line-height: 1;
  box-sizing: content-box;
  transition: none;
}

.sidebar.collapsed .nav-item .text {
  display: none !important;
}

.nav-item:hover {
  background: rgba(255, 255, 255, 0.1);
  color: var(--text-secondary);
  transform: none;
}

.text {
  font-size: 0.95rem;
  transition: none;
  white-space: normal;
  word-wrap: break-word;
  line-height: 1.4;
}

.sidebar.collapsed .text {
  opacity: 0;
  width: 0;
  overflow: hidden;
}

.sidebar .router-link-active {
  background: rgba(255, 255, 255, 0.1);
  color: white !important;
  font-weight: 500;
}

.sidebar .router-link-active i {
  color: white !important;
}

.sidebar-footer {
  padding: 8px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  margin-top: auto;
  position: sticky;
  bottom: 0;
  z-index: 1;
}

.logout-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 12px;
  background: none;
  border: none;
  color: #fff;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.95rem;
  border-radius: 8px;
  gap: 12px;
}

.logout-btn i {
  font-size: 1.2rem;
  min-width: 24px;
  text-align: center;
  color: #fff;
}

.logout-btn .text {
  color: #fff;
}

.sidebar.collapsed .logout-btn {
  padding: 12px 0;
}

.logout-btn:hover {
  background: #1e40af;
  color: #fff;
}

.mobile-toggle-btn {
  display: none;
  position: fixed;
  top: 20px;
  left: 20px;
  z-index: 1000;
  background: var(--background-tertiary);
  border: none;
  color: var(--text-secondary);
  width: 48px;
  height: 48px;
  border-radius: 12px;
  cursor: pointer;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  padding: 0;
  overflow: hidden;
}

.mobile-toggle-btn.expanded {
  width: auto;
  border-radius: 12px 0 0 12px;
  background: var(--background-tertiary);
}

.hamburger {
  width: 24px;
  height: 20px;
  position: relative;
  margin: 0 auto;
  transform: rotate(0deg);
  transition: .5s ease-in-out;
  cursor: pointer;
}

.hamburger span {
  display: block;
  position: absolute;
  height: 2px;
  width: 100%;
  background: var(--text-secondary);
  border-radius: 2px;
  opacity: 1;
  left: 0;
  transform: rotate(0deg);
  transition: .25s ease-in-out;
}

.hamburger span:nth-child(1) {
  top: 0px;
}

.hamburger span:nth-child(2) {
  top: 9px;
}

.hamburger span:nth-child(3) {
  top: 18px;
}

.mobile-toggle-btn.expanded .hamburger span:nth-child(1) {
  top: 9px;
  transform: rotate(135deg);
}

.mobile-toggle-btn.expanded .hamburger span:nth-child(2) {
  opacity: 0;
  left: -60px;
}

.mobile-toggle-btn.expanded .hamburger span:nth-child(3) {
  top: 9px;
  transform: rotate(-135deg);
}

.sidebar-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 99;
  backdrop-filter: blur(2px);
  opacity: 0;
  animation: fadeIn 0.3s ease forwards;
  display: none;
}

.sidebar-overlay[v-show="true"] {
  display: block;
}

.sidebar.mobile {
  position: fixed;
  left: -280px;
  top: 0;
  height: 100vh;
  margin: 0;
  border-radius: 0;
  width: 280px;
  transition: left 0.3s ease;
  z-index: 100;
  padding-top: 70px;
}

.sidebar.mobile.mobile-open {
  left: 0;
}

@media (max-width: 768px) {
  .mobile-toggle-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 101;
  }

  .mobile-toggle-btn.expanded {
    margin-left: 10px;
  }

  .sidebar {
    left: -280px;
    top: 0;
    height: 100vh;
    margin: 0;
    border-radius: 0;
    width: 280px;
    background: linear-gradient(180deg, #1e40af 60%, #1e3a8a 100%);
  }

  .sidebar.mobile-open {
    left: 0;
  }

  /* Remove the transform styles since we're using left positioning */
  .sidebar.mobile {
    transform: none;
  }

  .sidebar.mobile.collapsed {
    transform: none;
  }

  .nav-item {
    padding: 12px 20px;
    margin: 4px 12px;
    border-radius: 8px;
    transition: all 0.3s ease;
  }

  .nav-item:hover {
    background: rgba(255, 255, 255, 0.15);
    transform: translateX(4px);
  }

  .sidebar.collapsed .nav-item {
    padding: 12px 20px;
    margin: 4px 12px;
  }

  .text {
    opacity: 1 !important;
    width: auto !important;
    overflow: visible !important;
  }

  .sidebar-header {
    display: none;
  }

  .sidebar-nav {
    border-top: none;
    padding-top: 0;
  }
}

@media (max-width: 480px) {
  .mobile-toggle-btn {
    top: 10px;
    left: 10px;
    width: 40px;
    height: 40px;
  }

  .mobile-toggle-btn.expanded {
    width: 260px;
  }

  .hamburger {
    width: 20px;
    height: 16px;
  }

  .hamburger span:nth-child(2) {
    top: 7px;
  }

  .hamburger span:nth-child(3) {
    top: 14px;
  }

  .sidebar {
    width: 260px;
    left: -260px;
  }

  .sidebar.mobile-open {
    left: 0;
  }
}

/* Mobile Navbar Styles */
.mobile-navbar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: white;
  color: #1e40af;
  z-index: 1000;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.navbar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  height: 60px;
  border-bottom: 1px solid #e2e8f0;
}

.navbar-header .logo {
  font-size: 1.2rem;
  margin: 0;
  color: #1e40af;
}

.navbar-header .toggle-btn {
  background: none;
  border: none;
  color: #1e40af;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.navbar-header .toggle-btn:hover {
  background: #f1f5f9;
}

.navbar-nav {
  background: white;
  max-height: calc(100vh - 60px);
  overflow-y: auto;
  transition: all 0.3s ease;
}

.navbar-nav ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.navbar-nav .nav-item {
  display: flex;
  align-items: center;
  padding: 12px 20px;
  color: #1e40af;
  text-decoration: none;
  transition: all 0.3s ease;
  gap: 12px;
  border-bottom: 1px solid #e2e8f0;
  -webkit-tap-highlight-color: transparent;
  outline: none;
}

.navbar-nav .nav-item:active {
  color: #1e40af;
  background: #f1f5f9;
}

.navbar-nav .nav-item i {
  font-size: 1.2rem;
  min-width: 24px;
  text-align: center;
  color: #1e40af;
}

.navbar-nav .nav-item:active i {
  color: #1e40af;
}

.navbar-nav .nav-item .text {
  font-size: 0.95rem;
  color: #1e40af;
}

.mobile-navbar .router-link-active {
  background: #f1f5f9;
  color: #1e40af !important;
  font-weight: 600;
}

.mobile-navbar .router-link-active i {
  color: #1e40af !important;
}

/* Keep management dropdown items readable on white mobile nav */
.mobile-navbar .submenu {
  padding-left: 0;
  background: #fff;
}

.mobile-navbar .submenu-link {
  color: #1e40af;
  background: transparent;
  border-left: 0;
  padding: 10px 44px;
  font-weight: 500;
}

.mobile-navbar .submenu-link .text {
  color: #1e40af;
}

.mobile-navbar .submenu-link:hover,
.mobile-navbar .submenu-link:focus-visible {
  background: #f8fafc;
  color: #1e40af;
}

.mobile-navbar .submenu-link.submenu-link-active {
  background: #eff6ff;
  color: #1e40af;
  font-weight: 600;
}

.logout-btn {
  color: #fff;
}

.logout-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
}

/* Mobile navbar logout button overrides (white background nav) */
.mobile-navbar .logout-btn {
  width: 100%;
  padding: 12px 20px;
  border-radius: 0;
  border-top: 1px solid #e2e8f0;
  background: white;
  color: #1e40af;
  justify-content: flex-start;
}

.mobile-navbar .logout-btn i {
  color: #1e40af;
}

.mobile-navbar .logout-btn .text {
  color: #1e40af;
}

.mobile-navbar .logout-btn:hover {
  background: #f1f5f9;
  color: #1e40af;
}

@media (max-width: 768px) {
  .sidebar {
    display: none;
  }
  
  .mobile-navbar {
    display: block;
  }
  
  .navbar-nav {
    display: none;
  }
  
  .mobile-navbar.expanded .navbar-nav {
    display: block;
  }
}

@media (max-width: 480px) {
  .navbar-header {
    padding: 8px 16px;
    height: 50px;
  }
  
  .navbar-header .logo {
    font-size: 1.1rem;
  }
  
  .navbar-header .toggle-btn {
    font-size: 1.3rem;
    padding: 6px;
  }
  
  .navbar-nav .nav-item {
    padding: 10px 16px;
  }
}

.logo-img {
  height: 36px;
  width: auto;
  display: block;
  margin-right: 10px;
}

.sidebar-header.collapsed-header .logo-img {
  margin-right: 0;
}

@media (max-width: 480px) {
  .logo-img {
    height: 28px;
  }
}

.overview-item {
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.overview-item i.fa-chevron-right,
.overview-item i.fa-chevron-down {
  font-size: 0.8rem;
  margin-left: auto;
  transition: transform 0.3s ease;
}

.overview-item i.fa-chevron-down {
  transform: rotate(0deg);
}

.overview-item i.fa-chevron-right {
  transform: rotate(0deg);
}

.submenu {
  list-style: none;
  padding: 0;
  margin: 0;
  padding-left: 20px;
  overflow: hidden;
}

.submenu-item {
  padding: 8px 20px !important;
  font-size: 0.9rem;
  opacity: 0.9;
  transition: all 0.3s ease;
}

.submenu-link {
  display: block;
  width: 100%;
  padding: 10px 20px;
  margin: 0;
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  border-radius: 0;
  cursor: pointer;
  position: relative;
  z-index: 2;
  transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease;
  border-left: 3px solid transparent;
  background: transparent;
  font-weight: 500;
}

.submenu-link:not(.router-link-active):not(.submenu-link-active):hover,
.submenu-link:not(.router-link-active):not(.submenu-link-active):focus-visible {
  background: transparent;
  color: #fff;
  border-left-color: transparent;
}

.submenu-link.router-link-active,
.submenu-link.submenu-link-active {
  background: rgba(255, 255, 255, 0.18);
  color: #fff;
  border-left-color: #93c5fd;
  font-weight: 600;
}

.submenu-link.router-link-active:hover,
.submenu-link.submenu-link-active:hover {
  background: rgba(255, 255, 255, 0.18);
  color: #fff;
}

.dashboard-submenu-link.router-link-active:not(.submenu-link-active),
.dashboard-submenu-link.router-link-exact-active:not(.submenu-link-active) {
  background: transparent;
  color: rgba(255, 255, 255, 0.8);
  border-left-color: transparent;
  font-weight: 500;
}

/* Dropdown Animation */
.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s ease;
  max-height: 500px;
  opacity: 1;
}

.slide-enter-from,
.slide-leave-to {
  max-height: 0;
  opacity: 0;
  transform: translateY(-10px);
}

/* Mobile styles */
@media (max-width: 768px) {
  .submenu {
    padding-left: 16px;
  }
  
  .submenu-item {
    padding: 8px 16px !important;
  }
}
</style> 